import React, {useMemo} from 'react'
import {selectCartItems} from '../../store/slices/shoppingCartSlice'
import {selectCartValidationErrors} from '../../store/slices/shoppingCartValidationSlice'
import {useAppSelector} from '../../store/hooks'
import * as types from '../../types'
import ShoppingCartItem from './ShoppingCartItem'
import clsx from 'clsx'
import {formatCurrency} from '../../services/utils'
import {getLogger} from '../../services/logging'
import EmptyCart from '../../assets/ibe-empty-cart.png'
import {calcCartPriceSummary} from '../../services/offerUtils'

const logger = getLogger('ShoppingCart')

export interface ShoppingCartProps {
  className?: string
  validationMode?: boolean
}

const ShoppingCart = ({
  className = '',
  validationMode = false
}: ShoppingCartProps) => {
  const cartItems = useAppSelector(selectCartItems)
  const cartValidationErrors = useAppSelector(selectCartValidationErrors)
  const validationErrorLookup = cartValidationErrors.reduce(
    (acc: {[key: string]: types.ShoppingCartValidationError}, error) => {
      acc[error.id] = error
      return acc
    },
    {}
  )
  const cartTotals = useMemo(() => {
    return calcCartPriceSummary(cartItems)
  }, [cartItems])
  const hasPrepayment = cartTotals.totalPrepayment > 0

  logger.debug({
    message: 'Rendering ShoppingCart...',
    data: {cartItems, cartTotals}
  })

  return (
    <div className={clsx('flex flex-col', className)}>
      {!cartItems || cartItems.length === 0 ? (
        <div className="mt-8 flex flex-col gap-4 items-center">
          <img
            src={EmptyCart}
            alt="Shopping cart is empty image"
            className="max-h-72"
          />
          <h2 className="text-xl font-semibold">
            Your shopping cart is empty.
          </h2>
        </div>
      ) : (
        <>
          <div className="card card-compact border bg-white shadow w-full">
            <div className="card-body flex flex-col items-end gap-2 w-full">
              {/* Shopping Cart Items */}
              <div className="flex flex-col gap-2 w-full">
                {cartItems.map(
                  (item: types.ShoppingCartItem, index: number) => {
                    const validationError = validationMode
                      ? validationErrorLookup[item.id]
                      : null
                    return (
                      <div key={item.id} className="w-full">
                        <ShoppingCartItem
                          item={item}
                          itemIndex={index}
                          showActions={!validationMode}
                          showItemIndex={cartItems?.length > 1}
                          validationError={validationError}
                        />
                        <div className="divider w-full border-1 border-gray-200"></div>
                      </div>
                    )
                  }
                )}
              </div>

              {/* Shopping Cart Totals */}
              <div className="flex flex-row gap-1 text-right">
                <span className="text-md font-semibold">Total:</span>
                <span className="text-md font-bold w-20">
                  {formatCurrency(cartTotals.total)}
                </span>
              </div>

              {hasPrepayment && (
                <div className="flex flex-row gap-1 text-right">
                  <div className="flex flex-col">
                    <span className="text-md font-semibold">
                      * Due at Checkout:
                    </span>
                  </div>
                  <span className="text-md font-bold w-20">
                    {formatCurrency(cartTotals.totalPrepayment)}
                  </span>
                </div>
              )}

              {hasPrepayment && (
                <div className="text-right w-full text-xs text-gray-400 mt-2">
                  * All non-refundable room rates require full prepayment at
                  time of booking.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ShoppingCart
